import React, { Fragment, type ReactNode, useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { CodeBlock } from '@atlaskit/code';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import BugIcon from '@atlaskit/icon/core/bug';
import CopyIcon from '@atlaskit/icon/core/copy';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import Link from '@atlaskit/link';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Grid, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { QueryIntent } from '../../constants';
import { CopyButton } from '../common/ui/copy-button';

import { messages } from './messages';
import { MinionInvocationItem } from './minions-output';
import { PluginInvocationItem } from './plugin-invocation';
import type { MinionOutput, PluginInvocation } from './types';

const ColoredBugIcon = () => (
	<BugIcon color={token('color.icon.subtlest')} LEGACY_size="small" label="" />
);

const panelStyles = cssMap({
	root: {
		width: '100%',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
	twoColumnGrid: {
		gridTemplateColumns: '1fr 1fr',
	},
	copyJsonButton: {
		marginLeft: 'auto',
	},
});

export const Panel = ({ children, testId }: { children: ReactNode; testId?: string }) => (
	<Stack space="space.100" xcss={panelStyles.root} testId={testId}>
		{children}
	</Stack>
);

export const OnlineDebuggerButton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { commonAttributes, smartAnswersRoute } = useAIAnswerContext();
	const [isSAINDebuggerModalOpen, setIsSAINDebuggerModalOpen] = useState(false);
	const [jsonCopied, setJsonCopied] = useState(false);

	const isNaturalLanguageQuery = smartAnswersRoute === QueryIntent.NATURAL_LANGUAGE_QUERY;

	const openDebuggerModal = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setIsSAINDebuggerModalOpen(true);
	}, []);
	const closeDebuggerModal = useCallback(() => setIsSAINDebuggerModalOpen(false), []);

	const { extraAttributes } = commonAttributes;

	const formatJson = (data: unknown): string => {
		return JSON.stringify(data, null, 2);
	};

	const copyFinalResponseJson = async (): Promise<void> => {
		try {
			if (!extraAttributes?.rawJson) {
				return;
			}

			const jsonString = formatJson(extraAttributes.rawJson);
			if (!jsonString) {
				return;
			}

			await navigator.clipboard.writeText(jsonString);
			setJsonCopied(true);

			setTimeout(() => {
				setJsonCopied(false);
			}, 2500);

			return;
		} catch {
			return;
		}
	};

	return (
		<Fragment>
			<IconButton
				icon={ColoredBugIcon}
				label={formatMessage(messages.ai_debugger_icon_label)}
				onClick={openDebuggerModal}
				appearance="subtle"
				isTooltipDisabled={false}
				spacing="compact"
				testId="sain-online-debugger-button"
				color={token('color.icon.subtlest')}
				shape={fg('kd_sain_feedback_collector_ux_uplift_rollout') ? 'circle' : 'default'}
			/>

			<ModalTransition>
				{isSAINDebuggerModalOpen && (
					<Modal onClose={closeDebuggerModal} width="large" testId="sain-online-debugger-modal">
						<ModalHeader hasCloseButton>
							<ModalTitle>{formatMessage(messages.ai_debugger_modal_title)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Tabs id="sain-online-debugger-tabs">
								<TabList>
									<Tab>{formatMessage(messages.ai_debugger_modal_tab_request_overview)}</Tab>
									{isNaturalLanguageQuery && (
										<Tab>{formatMessage(messages.ai_debugger_modal_tab_plugin_invocations)}</Tab>
									)}
									{isNaturalLanguageQuery &&
										Array.isArray(extraAttributes?.minion_outputs) &&
										extraAttributes.minion_outputs.length > 0 && (
											<Tab>{formatMessage(messages.ai_debugger_modal_tab_minions_invoked)}</Tab>
										)}
									{isNaturalLanguageQuery && (
										<Tab>{formatMessage(messages.ai_debugger_modal_tab_sources_used)}</Tab>
									)}
									{isNaturalLanguageQuery && (
										<Tab>{formatMessage(messages.ai_debugger_modal_tab_raw_json)}</Tab>
									)}
								</TabList>
								<TabPanel>
									<Panel>
										<Box>
											<Heading size="xsmall">
												{formatMessage(messages.ai_debugger_modal_original_query_subheading)}
											</Heading>
											<Text>{extraAttributes?.originalQuery ?? 'null'}</Text>
										</Box>
										<Box>
											<Heading size="xsmall">
												{formatMessage(messages.ai_debugger_modal_transformed_query_subheading)}
											</Heading>
											<Text>{extraAttributes?.transformedQuery ?? 'null'}</Text>
										</Box>
										<Grid xcss={panelStyles.twoColumnGrid} gap="space.200">
											<Box>
												<Heading size="xsmall">
													{formatMessage(messages.ai_debugger_modal_detected_intent_subheading)}
												</Heading>
												<Text>{smartAnswersRoute}</Text>
											</Box>
											<Box>
												<Heading size="xsmall">
													{formatMessage(messages.ai_debugger_modal_trace_id_subheading)}
												</Heading>
												<Inline space="space.100" alignBlock="start">
													<Text>{extraAttributes?.traceId ?? 'null'}</Text>
													{extraAttributes?.traceId && (
														<CopyButton
															copyText={extraAttributes?.traceId}
															copyLabel={formatMessage(
																messages.ai_debugger_modal_copy_trace_id_label,
															)}
															copiedLabel={formatMessage(
																messages.ai_debugger_modal_trace_id_copied_text,
															)}
															testId="sain-online-debugger-copy-trace-id-button"
														/>
													)}
												</Inline>
											</Box>
											{isNaturalLanguageQuery && (
												<Fragment>
													<Box>
														<Heading size="xsmall">
															{formatMessage(messages.ai_debugger_modal_model_subheading)}
														</Heading>
														<Text>
															{extraAttributes?.answerGenerationModel?.model_usage &&
															typeof extraAttributes.answerGenerationModel.model_usage === 'object'
																? (Object.keys(
																		extraAttributes.answerGenerationModel.model_usage,
																	)[0] ?? 'null')
																: 'null'}
														</Text>
													</Box>
													<Box>
														<Heading size="xsmall">
															{formatMessage(messages.ai_debugger_modal_cached_answer_subheading)}
														</Heading>
														<Text>
															{extraAttributes.cacheInfo?.cache_hit?.toString() ?? 'null'}
														</Text>
													</Box>
												</Fragment>
											)}
										</Grid>
									</Panel>
								</TabPanel>
								{isNaturalLanguageQuery && (
									<TabPanel>
										<Panel>
											{Array.isArray(extraAttributes?.pluginInvocations) &&
											extraAttributes.pluginInvocations.length > 0 ? (
												<Stack space="space.0">
													{extraAttributes.pluginInvocations.map(
														(plugin: PluginInvocation, index: number) => (
															<PluginInvocationItem
																key={plugin.plugin_invocation_id || index}
																plugin={plugin}
																index={index}
															/>
														),
													)}
												</Stack>
											) : (
												<>
													<Text>
														{formatMessage(messages.ai_debugger_modal_no_plugins_invoked)}
													</Text>
												</>
											)}
										</Panel>
									</TabPanel>
								)}
								{isNaturalLanguageQuery &&
									Array.isArray(extraAttributes?.minion_outputs) &&
									extraAttributes.minion_outputs.length > 0 && (
										<TabPanel>
											<Panel>
												<Stack space="space.0">
													{extraAttributes.minion_outputs.map(
														(minion: MinionOutput, index: number) => (
															<MinionInvocationItem
																key={minion.minion_id || index}
																minion={minion}
																index={index}
															/>
														),
													)}
												</Stack>
											</Panel>
										</TabPanel>
									)}
								{isNaturalLanguageQuery && (
									<TabPanel>
										<Panel>
											<Heading size="xsmall">
												{formatMessage(messages.ai_debugger_modal_sources_subheading)}
											</Heading>
											{Array.isArray(extraAttributes?.sources) &&
											extraAttributes.sources.length > 0 ? (
												<Stack space="space.100">
													{extraAttributes.sources.map((source, index) => (
														<Inline space="space.050" alignBlock="center">
															<Text key={source.ari}>{index + 1}.</Text>
															<Link href={source.url} target="_blank">
																{source.title}
															</Link>
														</Inline>
													))}
												</Stack>
											) : (
												<Text>{formatMessage(messages.ai_debugger_modal_no_sources_used)}</Text>
											)}
										</Panel>
									</TabPanel>
								)}
								{isNaturalLanguageQuery && (
									<TabPanel>
										<Panel>
											<Box xcss={panelStyles.copyJsonButton}>
												<Button
													appearance="primary"
													onClick={copyFinalResponseJson}
													iconBefore={jsonCopied ? SuccessIcon : CopyIcon}
												>
													{jsonCopied
														? formatMessage(messages.ai_debugger_modal_copy_button_copied_text)
														: formatMessage(messages.ai_debugger_modal_copy_button_text)}
												</Button>
											</Box>
											<CodeBlock
												language="json"
												text={formatJson(extraAttributes?.rawJson ?? null)}
												shouldWrapLongLines
											/>
										</Panel>
									</TabPanel>
								)}
							</Tabs>
						</ModalBody>
						<ModalFooter />
					</Modal>
				)}
			</ModalTransition>
		</Fragment>
	);
};
